/**
 * apps/wildswim-web/src/lib/firebase.ts
 *
 * Firebase Web SDK bootstrap. Uses VITE_FIREBASE_* env vars at build
 * time (same pattern as flagship apps/web). The values come from
 * the wildswim-web Cloud Build trigger substitutions.
 *
 * App Check provider: reCAPTCHA v3 site key. The site key is public
 * but locked to the registered domain (wildswim.hattieknows.com) so
 * a leaked key can't be reused. Token auto-refresh enabled so the
 * X-Firebase-AppCheck header on every API call gets a valid token
 * without explicit refresh logic on our side.
 */

import { initializeApp, type FirebaseOptions } from 'firebase/app';
import {
    initializeAppCheck,
    ReCaptchaV3Provider,
    getToken as getAppCheckTokenInternal,
    type AppCheck,
} from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import {
    getMessaging,
    getToken as getMessagingToken,
    deleteToken as deleteMessagingToken,
    isSupported as isMessagingSupported,
    type Messaging,
} from 'firebase/messaging';

const config: FirebaseOptions = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
    appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
    // **Required** for getMessaging(). Without it, FCM throws
    // 'messaging/missing-app-config-values' the moment we touch
    // it — even though the SW (which has its own hardcoded copy)
    // loads fine. Public ID; matches the value baked into
    // public/firebase-messaging-sw.js.
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
    storageBucket: 'wildswim-reports-photos',
};

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

const app = initializeApp(config);

export const auth = getAuth(app);

// App Check is **lazy-initialised** on the first call to
// getAppCheckToken(). Eagerly initialising at module-load time
// pulled the 360 KB recaptcha__en.js bundle from gstatic on
// EVERY page, including the landing + data + press marketing
// pages that make zero authenticated API calls — Lighthouse
// scored landing perf at 48 because of it. With lazy init the
// recaptcha script only loads when the user actually navigates
// to a page that hits /v1/* (Today, Map, Saved, etc).
//
// Init is cached in a module-level promise so concurrent first
// callers share the same initialisation pass.
let appCheck: AppCheck | null = null;
let appCheckInitPromise: Promise<AppCheck | null> | null = null;

function ensureAppCheck(): Promise<AppCheck | null> {
    if (appCheck) return Promise.resolve(appCheck);
    if (appCheckInitPromise) return appCheckInitPromise;
    if (!recaptchaSiteKey) return Promise.resolve(null);
    appCheckInitPromise = (async () => {
        try {
            appCheck = initializeAppCheck(app, {
                provider: new ReCaptchaV3Provider(recaptchaSiteKey),
                isTokenAutoRefreshEnabled: true,
            });
            return appCheck;
        } catch (err) {
            console.warn('[firebase] App Check init failed', err);
            return null;
        }
    })();
    return appCheckInitPromise;
}

export async function getAppCheckToken(): Promise<string | null> {
    const ac = await ensureAppCheck();
    if (!ac) return null;
    try {
        const result = await getAppCheckTokenInternal(ac, /* forceRefresh */ false);
        return result.token;
    } catch (err) {
        console.warn('[firebase] App Check token fetch failed', err);
        return null;
    }
}

// ---------------------------------------------------------------------------
// Web push (FCM) — lazy init, same shape as App Check
// ---------------------------------------------------------------------------

const vapidKey = import.meta.env['VITE_FIREBASE_VAPID_KEY'] as string | undefined;

let messaging: Messaging | null = null;
let messagingInitPromise: Promise<MessagingInit> | null = null;

interface MessagingInit {
    messaging: Messaging | null;
    /// Why init failed, if it did. Surfaces through requestPushToken
    /// so the user sees the real reason, not a generic "unsupported".
    failure: { code: 'no-vapid' | 'isMessagingSupported-false' | 'getMessaging-threw'; message: string } | null;
}

async function ensureMessaging(): Promise<MessagingInit> {
    if (messaging) return { messaging, failure: null };
    if (messagingInitPromise) return messagingInitPromise;
    messagingInitPromise = (async (): Promise<MessagingInit> => {
        let supported: boolean;
        try {
            supported = await isMessagingSupported();
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.warn('[firebase] isMessagingSupported threw', err);
            return { messaging: null, failure: { code: 'isMessagingSupported-false', message: msg } };
        }
        if (!supported) {
            return { messaging: null, failure: { code: 'isMessagingSupported-false', message: 'isMessagingSupported() returned false' } };
        }
        try {
            messaging = getMessaging(app);
            return { messaging, failure: null };
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.warn('[firebase] getMessaging threw', err);
            return { messaging: null, failure: { code: 'getMessaging-threw', message: msg } };
        }
    })();
    return messagingInitPromise;
}

/**
 * Whether the current browser supports web push at all. iOS Safari
 * (in a regular tab) returns false here — Apple requires the site
 * to be added to the Home Screen as a PWA before push works.
 */
export async function isPushSupported(): Promise<boolean> {
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
        return false;
    }
    const init = await ensureMessaging();
    return init.messaging != null;
}

/**
 * Request notification permission, register the service worker,
 * and return the FCM registration token. Resolves to null when:
 *   - the browser doesn't support push (iOS Safari outside a PWA)
 *   - the user denied permission
 *   - the VAPID key is missing
 *   - any step throws
 *
 * Caller (frontend hook) is responsible for POSTing the token to
 * /v1/me/push-subscriptions so the backend can fan out future
 * sewage alerts to it.
 */
export interface PushSetupError {
    code: 'no-vapid' | 'unsupported' | 'permission-denied' | 'sw-failed' | 'token-failed';
    message: string;
}

export async function requestPushToken(): Promise<{ token: string } | { error: PushSetupError }> {
    console.info('[push] requestPushToken: start', { hasVapid: !!vapidKey });
    if (!vapidKey) {
        return { error: { code: 'no-vapid', message: 'VAPID key missing in build env (VITE_FIREBASE_VAPID_KEY).' } };
    }
    // Pre-warm App Check BEFORE any FCM call. If the FCM API is App-Check-
    // enforced for this project (it is, in prod), getToken() will internally
    // request an App Check token — and if App Check has never been
    // initialised on the page (e.g. the user clicked Enable straight after
    // page-load without making any /v1 call first), getToken throws with a
    // confusing 'token-subscribe-failed' / 401 error. Explicitly priming
    // here removes the race.
    try {
        const ack = await getAppCheckToken();
        console.info('[push] App Check primed', { hasToken: !!ack });
    } catch (err) {
        console.warn('[push] App Check prime threw (continuing)', err);
    }
    const init = await ensureMessaging();
    console.info('[push] ensureMessaging', { ok: !!init.messaging, failure: init.failure });
    if (!init.messaging) {
        const f = init.failure;
        return { error: {
            code: 'unsupported',
            message: f
                ? `${f.code}: ${f.message}`
                : 'Push messaging not supported in this browser.',
        }};
    }
    const m = init.messaging;
    let perm: NotificationPermission;
    try {
        perm = await Notification.requestPermission();
        console.info('[push] permission result', perm);
    } catch (err) {
        return { error: { code: 'permission-denied', message: err instanceof Error ? err.message : 'Permission request failed.' } };
    }
    if (perm !== 'granted') {
        return { error: { code: 'permission-denied', message: `Permission ${perm}` } };
    }
    let registration: ServiceWorkerRegistration;
    try {
        // Register, then wait for the SW to be ready (active). Calling
        // getToken before the SW is ACTIVE is the most common cause of
        // 'messaging/failed-service-worker-registration' style errors.
        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        registration = await navigator.serviceWorker.ready;
        console.info('[push] SW ready', { scope: registration.scope, active: !!registration.active });
    } catch (err) {
        return { error: { code: 'sw-failed', message: err instanceof Error ? err.message : 'Service worker registration failed.' } };
    }
    try {
        const token = await getMessagingToken(m, {
            vapidKey,
            serviceWorkerRegistration: registration,
        });
        if (!token) {
            console.warn('[push] FCM returned an empty token');
            return { error: { code: 'token-failed', message: 'FCM returned an empty token.' } };
        }
        console.info('[push] FCM token acquired', { len: token.length });
        return { token };
    } catch (err) {
        // Surface the full FCM error structure — code + message + cause.
        // Helps us tell "App Check rejected" from "browser unsupported"
        // from "service worker scope mismatch" from a console paste.
        const e = err as { code?: string; message?: string; customData?: unknown; cause?: unknown };
        const msg = e?.message ?? String(err);
        console.error('[push] getToken failed', {
            code: e?.code,
            message: msg,
            customData: e?.customData,
            cause: e?.cause,
        });
        return { error: { code: 'token-failed', message: e?.code ? `${e.code}: ${msg}` : msg } };
    }
}

export async function revokePushToken(): Promise<void> {
    const init = await ensureMessaging();
    if (!init.messaging) return;
    try {
        await deleteMessagingToken(init.messaging);
    } catch (err) {
        console.warn('[firebase] revokePushToken failed', err);
    }
}
